import { useEffect, useRef } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isGloballyHandled } from '../../services/api';
import { signUpForCircle } from '../../services/circles.service';
import { getCohort } from '../../services/cohort.service';
import { useAuthStore } from '../../stores/auth.store';
import { goJustMissed, HUB_PATH, type JustMissedState } from './justMissed';
import { type PendingCircleJoin as Pending, takePendingJoin } from './pendingCircleJoin';
import { COHORT_GRACE_MS } from './publicNotice';
import type { SessionMissedState } from './SessionMissedNote';

export const PENDING_JOIN_COPY = {
  failed: 'Could not sign you up for that circle. Please try again from the Circles tab.',
} as const;

type Res = {
  status?: number;
  data?: { error?: string; message?: string; next_scheduled_at?: string };
};

function hub(navigate: NavigateFunction): void {
  const state: Pick<JustMissedState, 'tab'> = { tab: 'circles' };
  void navigate(HUB_PATH, { state, replace: true });
}

/**
 * The hourly circle: ask the server for T's circle by name. Before T it signs
 * them up (through signUp()'s locks, bans and the C13 credit check) and the
 * circle's waiting state takes them; after T the server answers 409 `missed`
 * (its clock, not this device's, S7) and they land on the hub's "You just
 * missed it" with the next circle one tap away. Never a signup for a circle
 * they did not choose (N14).
 *
 * The waiting state is the hub's OnDemandCard, not the circle's own route: a
 * signup only succeeds while the circle is `upcoming`, and CircleSession's
 * join() on an upcoming circle is a 422 shown as "This 1:1 session isn't
 * active" with nothing polling it back to life at T (Codex on #1099). The
 * card says "You're signed up!", counts down, and navigates into the room
 * itself once the circle is in_progress, so both states are routed by the
 * one surface that already watches them.
 */
async function runHourly(join: Extract<Pending, { kind: 'hourly' }>, navigate: NavigateFunction) {
  try {
    await signUpForCircle(join.startsAt);
    hub(navigate);
  } catch (e) {
    // 402: the global out-of-credits prompt already opened; nothing of our own.
    if (isGloballyHandled(e)) return;
    const res = (e as { response?: Res }).response;
    if (res?.status === 409 && res.data?.error === 'missed') {
      goJustMissed(navigate, res.data.next_scheduled_at ?? null);
      return;
    }
    // Class B: the server's reason reaches them (banned, closed, already in).
    toast.error(res?.data?.message || PENDING_JOIN_COPY.failed, { autoClose: 8000 });
    hub(navigate);
  }
}

/**
 * A public group's session: the room if it is live and still inside its
 * walk-in window (join() runs there as today, with the walk-in gate), the
 * group page if it has not started, and the group page with "This session
 * has started without you" once the window has passed.
 */
async function runCohort(join: Extract<Pending, { kind: 'cohort' }>, navigate: NavigateFunction) {
  const group = `/app/circles/cohorts/${join.cohortId}`;
  try {
    const cohort = await getCohort(join.cohortId);
    const now = Date.now();
    const live = cohort.active_session;
    const until = join.until
      ? Date.parse(join.until)
      : live?.started_at
        ? Date.parse(live.started_at) + COHORT_GRACE_MS
        : null;
    const open = until === null || now <= until;
    if (live && live.status === 'in_progress' && open) {
      void navigate(`/app/circles/${live.id}`, { replace: true });
      return;
    }
    if (open) {
      void navigate(group, { replace: true });
      return;
    }
    const state: SessionMissedState = {
      sessionMissed: { nextAt: cohort.next_session?.scheduled_at ?? null },
    };
    void navigate(group, { replace: true, state });
  } catch {
    // The group page reports its own errors (private, gone).
    void navigate(group, { replace: true });
  }
}

/**
 * Runs a signed-out visitor's pending join once they are in (docs/plans/
 * CIRCLE-STARTING-NOTICE.md §5, N12). Mounted ONCE, in ProtectedRoute,
 * BEHIND VerifyEmailGate: every way in (register, log in, Google, Apple)
 * lands there, and an unverified new account never reaches it (the server's
 * verified-new middleware refuses anyway). It takes the stored join once and
 * clears it, so a later sign-in never replays it.
 */
export default function PendingCircleJoin() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isGuest = useAuthStore((s) => s.isGuest);
  const ran = useRef(false);
  const verified = !!user && !(user.verification_required === true && user.email_verified !== true);

  useEffect(() => {
    if (ran.current || isGuest || !verified) return;
    ran.current = true;
    const join = takePendingJoin();
    if (!join) return;
    void (join.kind === 'hourly' ? runHourly(join, navigate) : runCohort(join, navigate));
  }, [verified, isGuest, navigate]);

  return null;
}
